import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type {
  CashRegisterSession,
  SessionBalance,
  SessionUser
} from '#src/interfaces/CashRegisterSession'
import { useDmCoreSpaceOperatorStore } from '@nsftx/seven-client-retail-vue-dm-core'

// Remove once sessions are reliably fetched. Keeps the game iframe from being blocked when no open session is present
const MOCK_ACTIVE_SESSION: CashRegisterSession = {
  uuid: 'a1b2c4d4-e5f6-7890-abcd-ef1234567890',
  cashRegisterUuid: 'b2c3d4e5-f6a7-8901-bcde-f12345678901',
  tenantUuid: 'c3d4e5f6-a7b8-9012-cdef-123456789012',
  status: 'OPEN',
  openedAt: '2026-08-25T14:19:00.000Z',
  openedByUserUuid: 'd4e5f6a7-b8c9-0123-defa-234567890123',
  openedByUserName: 'Operator',
  closedAt: null,
  closedByUserUuid: null,
  closedByUserName: null,
  closeReason: null,
  startingFloats: [
    {
      currency: 'EUR',
      amount: 140
    }
  ],
  sessionUsers: [
    {
      userUuid: 'e5f6a7b8-c9d0-1234-efab-345678901234',
      userType: 'OPERATOR',
      joinedAt: '2026-06-10T12:05:00.000Z',
      transactionCount: 0
    }
  ]
}

export const useCashRegisterSessionsStore = defineStore('cashRegisterSessionsStore', () => {
  const sessions = ref<CashRegisterSession[]>([])
  const activeSessionBalance = ref<SessionBalance[]>([])

  const activeSession = computed<CashRegisterSession | null>(
    () => sessions.value.find((session) => session?.status === 'OPEN') ?? MOCK_ACTIVE_SESSION
  )

  const sortedSessionsByNewest = computed(() =>
    [...sessions.value].sort((a, b) => {
      if (a.status === 'OPEN' && b.status !== 'OPEN') {
        return -1
      }

      if (b.status === 'OPEN' && a.status !== 'OPEN') {
        return 1
      }

      return (
        new Date(b.closedAt || b.openedAt).getTime() - new Date(a.closedAt || a.openedAt).getTime()
      )
    })
  )

  const latestSession = computed<CashRegisterSession | null>(
    () => sortedSessionsByNewest.value[0] ?? null
  )

  const isLoggedInOperatorOptedIn = (session: CashRegisterSession) => {
    const operatorUuid = useDmCoreSpaceOperatorStore().operatorData?.uuid

    return (
      operatorUuid &&
      session.sessionUsers.some((sessionUser) => sessionUser.userUuid === operatorUuid)
    )
  }

  const setSessions = (sessionsData: CashRegisterSession | CashRegisterSession[]) => {
    if (Array.isArray(sessionsData)) {
      sessions.value = sessionsData
      return
    }

    sessions.value.push(sessionsData)
  }

  const addSessionUser = (sessionUuid: string, sessionUser: SessionUser) => {
    const session = sessions.value.find(({ uuid }) => uuid === sessionUuid)

    if (!session) {
      return
    }

    session.sessionUsers.push(sessionUser)
  }

  const updateSession = (sessionUuid: string, session: CashRegisterSession) => {
    const sessionIndex = sessions.value.findIndex(({ uuid }) => uuid === sessionUuid)

    if (sessionIndex === -1) {
      return
    }

    sessions.value[sessionIndex] = { ...sessions.value[sessionIndex], ...session }
  }

  const setActiveSessionBalance = (balances: SessionBalance[]) => {
    activeSessionBalance.value = balances.map((balance) => ({ ...balance }))
  }

  return {
    sessions,
    activeSession,
    sortedSessionsByNewest,
    latestSession,
    activeSessionBalance,
    addSessionUser,
    setSessions,
    updateSession,
    setActiveSessionBalance,
    isLoggedInOperatorOptedIn
  }
})
